import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { fetchCustomers, fetchLicenses, fetchLogs, fetchServers, updateLicense, fetchNetworkInsights } from '../api/admin';
import { RenewLicenseDialog } from '../components/RenewLicenseDialog';
import { LicenseDetailsDrawer } from '../components/LicenseDetailsDrawer';
import { Tooltip, Snackbar } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from '@mui/icons-material/Check';
import { MachineActionsManager } from '../components/MachineActionsManager';
import { MachineIpDetailsDialog } from '../components/MachineIpDetailsDialog';

const formatLastSeen = (timestampMs?: number) => {
    if (!timestampMs) return '-';
    const seconds = Math.floor((Date.now() - timestampMs) / 1000);
    if (seconds < 0 || seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Yesterday';
    return `${days} days ago`;
};

const timeAgo = (timestampSec?: number) => {
    if (!timestampSec) return '-';
    const seconds = Math.floor(Date.now() / 1000 - timestampSec);
    if (seconds < 0 || seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Yesterday';
    return `${days} days ago`;
};

export function CustomerDetailPage() {
    const { customerId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // UI States
    const [activeTab, setActiveTab] = useState<'licenses' | 'machines' | 'logs' | 'machine-network'>('licenses');
    const [licensesSearchTerm, setLicensesSearchTerm] = useState('');
    const [machinesSearchTerm, setMachinesSearchTerm] = useState('');
    const [selectedLicense, setSelectedLicense] = useState<any>(null);
    const [licenseToRenew, setLicenseToRenew] = useState<any>(null);
    const [renewDialogOpen, setRenewDialogOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    // Machine IP Details Dialog state
    const [selectedMachineForIpDetails, setSelectedMachineForIpDetails] = useState<any>(null);

    // Machine Network Insights Table states (filters, search, pagination)
    const [networkSearchTerm, setNetworkSearchTerm] = useState('');
    const [networkLicenseFilter, setNetworkLicenseFilter] = useState('');
    const [networkMachineFilter, setNetworkMachineFilter] = useState('');
    const [networkCurrentPage, setNetworkCurrentPage] = useState(1);
    const [machineSearchQuery, setMachineSearchQuery] = useState('');
    const [showMachineDropdown, setShowMachineDropdown] = useState(false);

    // Logs cursor-based pagination state
    const [cursorStack, setCursorStack] = useState<(number | null)[]>([null]);
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const logsLimit = 15;

    // Machine Actions State
    const [actionServer, setActionServer] = useState<any>(null);
    const [actionType, setActionType] = useState<'block' | 'unblock' | 'deactivate' | 'replace' | 'reactivate' | null>(null);
    const [openMenuServerId, setOpenMenuServerId] = useState<string | null>(null);

    const showMessage = (msg: string) => {
        setSnackbarMessage(msg);
        setSnackbarOpen(true);
    };

    // Customer Identity Query
    const { data: customersData, isLoading: customerLoading } = useQuery({
        queryKey: ['customers'],
        queryFn: fetchCustomers,
    });
    const customers = customersData?.customers || [];
    const customer = customers.find((c: any) => c.id === customerId || c.customerId === customerId);

    // Licenses Query
    const { data: licensesData, isLoading: licensesLoading } = useQuery({
        queryKey: ['licenses', { customerId }],
        queryFn: () => fetchLicenses({ customerId }),
        enabled: !!customerId,
    });
    const customerLicenses = licensesData?.licenses || [];

    const currentCursor = cursorStack[currentPageIndex];

    // Logs Query (Lazy load timeline logs)
    const { data: logsData, isLoading: logsLoading } = useQuery({
        queryKey: ['logs', { customerId, cursor: currentCursor }],
        queryFn: () => fetchLogs({ customerId, limit: logsLimit, startAfter: currentCursor || undefined }),
        enabled: !!customerId && activeTab === 'logs',
        placeholderData: keepPreviousData,
    });
    const logs = logsData?.logs || [];
    const nextCursor = logsData?.nextCursor || null;

    // Servers Query (Lazy load machines grid)
    const { data: serversData, isLoading: serversLoading } = useQuery({
        queryKey: ['servers'],
        queryFn: fetchServers,
        enabled: !!customerId && (activeTab === 'machines' || activeTab === 'machine-network'),
    });
    const servers = serversData?.servers || [];

    // Network Insights Query
    const { data: insightsData, isLoading: insightsLoading } = useQuery({
        queryKey: ['network-insights'],
        queryFn: fetchNetworkInsights,
        enabled: !!customerId && activeTab === 'machine-network',
    });
    const insights = insightsData?.insights || [];

    // Reset pagination to page 1 on search/filter changes
    useEffect(() => {
        setNetworkCurrentPage(1);
    }, [networkSearchTerm, networkLicenseFilter, networkMachineFilter]);

    // Filter servers associated with customer's license keys
    const customerLicenseKeys = new Set<string>(customerLicenses.map((l: any) => l.licenseKey));
    const customerServers = servers.filter((s: any) => customerLicenseKeys.has(s.licenseKey));

    // Prepare mapped machine network insights list
    const mappedNetworkInsights = customerServers.map((srv: any) => {
        const insight = insights.find((ins: any) => ins.machineId === srv.serverId || ins.id === srv.serverId);
        return {
            machineId: srv.serverId,
            machineName: srv.serverName || 'Unknown',
            licenseKey: srv.licenseKey,
            ipAddress: srv.ipAddress || '',
            lastSeenAt: srv.lastSeenAt,
            isp: insight?.network?.type === 'private_network' ? 'Private Network' : (insight?.network?.isp || ''),
            asn: insight?.network?.type === 'private_network' ? '-' : (insight?.network?.asn || ''),
            domain: insight?.network?.type === 'private_network' ? '-' : (insight?.network?.domain || ''),
            country: insight?.network?.type === 'private_network' ? '-' : (insight?.network?.country || ''),
            countryCode: insight?.network?.type === 'private_network' ? '-' : (insight?.network?.countryCode || ''),
            type: insight?.network?.type || null,
            lastCheckedAt: insight?.lastCheckedAt || null,
            lastIpChangedAt: insight?.lastIpChangedAt || null,
            rawMachine: srv,
            rawInsight: insight || null
        };
    });

    // Apply search and filters
    const filteredNetworkInsights = mappedNetworkInsights.filter((item: any) => {
        if (networkLicenseFilter && item.licenseKey !== networkLicenseFilter) {
            return false;
        }
        if (networkMachineFilter && item.machineId !== networkMachineFilter) {
            return false;
        }
        if (networkSearchTerm) {
            const term = networkSearchTerm.toLowerCase();
            const matchesSearch =
                item.machineName.toLowerCase().includes(term) ||
                item.machineId.toLowerCase().includes(term) ||
                item.licenseKey.toLowerCase().includes(term) ||
                item.ipAddress.toLowerCase().includes(term) ||
                item.isp.toLowerCase().includes(term) ||
                item.asn.toLowerCase().includes(term) ||
                item.domain.toLowerCase().includes(term) ||
                item.country.toLowerCase().includes(term);
            if (!matchesSearch) return false;
        }
        return true;
    });

    // Apply pagination (15 items per page)
    const networkItemsPerPage = 15;
    const totalNetworkItems = filteredNetworkInsights.length;
    const totalNetworkPages = Math.ceil(totalNetworkItems / networkItemsPerPage) || 1;
    const paginatedNetworkInsights = filteredNetworkInsights.slice(
        (networkCurrentPage - 1) * networkItemsPerPage,
        networkCurrentPage * networkItemsPerPage
    );

    // Status Update Mutation
    const updateMutation = useMutation({
        mutationFn: ({ licenseKey, data }: { licenseKey: string; data: any }) => updateLicense(licenseKey, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['licenses'] });
            showMessage('License updated successfully');
        },
        onError: (err: any) => {
            showMessage(err.response?.data?.error || 'Operation failed');
        }
    });

    const handleUpdateStatus = async (licenseKey: string, status: 'active' | 'suspended') => {
        await updateMutation.mutateAsync({ licenseKey, data: { status } });
    };

    if (customerLoading) {
        return <div className="text-center py-8 text-gray-500">Loading customer details...</div>;
    }

    if (!customer) {
        return (
            <div className="text-center py-8 text-red-500">
                Customer not found. <button onClick={() => navigate('/customers')} className="underline font-semibold">Back to list</button>
            </div>
        );
    }

    // Metric Counts
    const totalLicenses = customerLicenses.length;
    const activeLicenses = customerLicenses.filter((l: any) => l.status === 'active').length;
    const suspendedLicenses = customerLicenses.filter((l: any) => l.status === 'suspended').length;
    const totalMachinesCount = customerServers.length;

    // Filter Licenses by search term
    const filteredLicenses = customerLicenses.filter((l: any) => {
        const term = licensesSearchTerm.toLowerCase();
        return (
            l.licenseKey.toLowerCase().includes(term) ||
            l.productId.toLowerCase().includes(term)
        );
    });

    // Filter Machines by search term
    const filteredMachines = customerServers.filter((m: any) => {
        const term = machinesSearchTerm.toLowerCase();
        return (
            (m.serverName || '').toLowerCase().includes(term) ||
            (m.fingerprint || '').toLowerCase().includes(term) ||
            (m.ipAddress || '').toLowerCase().includes(term) ||
            (m.licenseKey || '').toLowerCase().includes(term)
        );
    });

    return (
        <div className="max-w-6xl mx-auto space-y-8 pb-10">
            {/* Breadcrumb Header */}
            <div>
                <button
                    onClick={() => navigate('/customers')}
                    className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-350 flex items-center gap-1.5 mb-3 cursor-pointer"
                >
                    ← Back to Customers
                </button>
                <div className="text-[10px] tracking-[0.25em] text-[#c87a53] uppercase font-bold mb-1">
                    CUSTOMER ACCOUNT HUB
                </div>
                <h1 className="text-3xl font-extrabold text-[#0b1329] dark:text-white tracking-tight">
                    {customer.companyName}
                </h1>
            </div>

            {/* Posture Metrics Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <div className="p-5 rounded-2xl border border-gray-150/40 dark:border-slate-800/60 shadow-sm bg-slate-100/90 dark:bg-slate-900/40 flex flex-col justify-between h-28">
                    <div className="flex justify-between items-start">
                        <span className="text-[10px] tracking-wider uppercase font-bold text-slate-500 dark:text-slate-400">Total Licenses</span>
                        <span className="text-lg">🎫</span>
                    </div>
                    <div className="text-2xl font-extrabold tracking-tight text-slate-800 dark:text-slate-100">{totalLicenses}</div>
                </div>

                <div className="p-5 rounded-2xl border border-gray-150/40 dark:border-slate-800/60 shadow-sm bg-[#dcfce7] dark:bg-emerald-950/20 flex flex-col justify-between h-28">
                    <div className="flex justify-between items-start">
                        <span className="text-[10px] tracking-wider uppercase font-bold text-emerald-700 dark:text-emerald-400">Active Licenses</span>
                        <span className="text-lg">▶️</span>
                    </div>
                    <div className="text-2xl font-extrabold tracking-tight text-[#15803d] dark:text-[#4ade80]">{activeLicenses}</div>
                </div>

                <div className="p-5 rounded-2xl border border-gray-150/40 dark:border-slate-800/60 shadow-sm bg-[#fef3c7] dark:bg-amber-950/20 flex flex-col justify-between h-28">
                    <div className="flex justify-between items-start">
                        <span className="text-[10px] tracking-wider uppercase font-bold text-amber-700 dark:text-amber-400">Suspended Licenses</span>
                        <span className="text-lg">⏸️</span>
                    </div>
                    <div className="text-2xl font-extrabold tracking-tight text-[#9a3412] dark:text-[#fb923c]">{suspendedLicenses}</div>
                </div>

                <div className="p-5 rounded-2xl border border-gray-150/40 dark:border-slate-800/60 shadow-sm bg-blue-50 dark:bg-blue-950/20 flex flex-col justify-between h-28">
                    <div className="flex justify-between items-start">
                        <span className="text-[10px] tracking-wider uppercase font-bold text-blue-700 dark:text-blue-400">Active Machines</span>
                        <span className="text-lg">🖥️</span>
                    </div>
                    <div className="text-2xl font-extrabold tracking-tight text-blue-800 dark:text-blue-300">{totalMachinesCount}</div>
                </div>
            </div>

            {/* Profile Identity Details Card */}
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-sm border border-gray-150 dark:border-gray-700/60 p-6">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 tracking-tight">
                    Customer Details Profile
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Contact Person</p>
                        <p className="text-gray-800 dark:text-gray-200 font-medium">{customer.contactPerson || 'Not provided'}</p>
                    </div>
                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Email Address</p>
                        <p className="text-gray-800 dark:text-gray-200 font-mono font-medium">{customer.email || 'Not provided'}</p>
                    </div>
                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Phone Number</p>
                        <p className="text-gray-800 dark:text-gray-200 font-medium">{customer.phone || 'Not provided'}</p>
                    </div>
                    {customer.notes && (
                        <div className="md:col-span-3 bg-slate-50 dark:bg-slate-900/30 p-4 rounded-2xl border dark:border-gray-700/65">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Administrative Notes</p>
                            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{customer.notes}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Segmented/Tab Operations Navigation */}
            <div className="flex border-b border-gray-200 dark:border-gray-700 gap-2">
                <button
                    onClick={() => setActiveTab('licenses')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition cursor-pointer ${
                        activeTab === 'licenses'
                            ? 'border-primary-500 text-primary-600 dark:text-primary-400 font-semibold'
                            : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-350'
                    }`}
                >
                    Licenses ({totalLicenses})
                </button>
                <button
                    onClick={() => setActiveTab('machines')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition cursor-pointer ${
                        activeTab === 'machines'
                            ? 'border-primary-500 text-primary-600 dark:text-primary-400 font-semibold'
                            : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-350'
                    }`}
                >
                    Activated Machines ({totalMachinesCount})
                </button>
                <button
                    onClick={() => setActiveTab('logs')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition cursor-pointer ${
                        activeTab === 'logs'
                            ? 'border-primary-500 text-primary-600 dark:text-primary-400 font-semibold'
                            : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-350'
                    }`}
                >
                    Activity Timeline
                </button>
                <button
                    onClick={() => setActiveTab('machine-network')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition cursor-pointer ${
                        activeTab === 'machine-network'
                            ? 'border-primary-500 text-primary-600 dark:text-primary-400 font-semibold'
                            : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-350'
                    }`}
                >
                    ISP Insights
                </button>
            </div>

            {/* Tab Contents */}
            <div>
                {activeTab === 'licenses' && (
                    <div className="space-y-4">
                        {/* Search and Filters */}
                        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-sm border border-gray-150/40 dark:border-gray-700/60 p-4">
                            <div className="max-w-md">
                                <input
                                    placeholder="Search licenses by key or product..."
                                    value={licensesSearchTerm}
                                    onChange={(e) => setLicensesSearchTerm(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                />
                            </div>
                        </div>

                        {licensesLoading ? (
                            <div className="text-center py-8 text-gray-500">Loading licenses...</div>
                        ) : filteredLicenses.length === 0 ? (
                            <div className="text-center py-12 text-gray-500 bg-white dark:bg-gray-800 rounded-3xl border border-gray-150 dark:border-gray-700/60 font-medium">
                                {licensesSearchTerm ? 'No licenses matched your search.' : 'No licenses assigned to this customer.'}
                            </div>
                        ) : (
                            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-sm border border-gray-150 dark:border-gray-700/60 overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead className="bg-slate-50 dark:bg-slate-900/30">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    License Key
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Product ID
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Status
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Expiry Date
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Machines Limit
                                                </th>
                                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                                            {filteredLicenses.map((license: any) => (
                                                <tr
                                                    key={license.licenseKey}
                                                    onClick={() => setSelectedLicense(license)}
                                                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/35 transition-colors"
                                                >
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center gap-2 font-mono text-sm text-slate-800 dark:text-slate-100 font-semibold" onClick={(e) => e.stopPropagation()}>
                                                            <span onClick={() => setSelectedLicense(license)} className="cursor-pointer hover:underline">{license.licenseKey}</span>
                                                            <Tooltip title="Copy Key" arrow>
                                                                <button
                                                                    onClick={() => {
                                                                        navigator.clipboard.writeText(license.licenseKey);
                                                                        showMessage('License key copied');
                                                                    }}
                                                                    className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded text-slate-500 cursor-pointer"
                                                                >
                                                                    <ContentCopyIcon className="!w-3 !h-3" />
                                                                </button>
                                                            </Tooltip>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-650 dark:text-gray-300">
                                                        {license.productId}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <span className={`inline-block px-2.5 py-0.5 text-xs font-medium rounded-full ${
                                                            license.status === 'active'
                                                                ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                                                                : license.status === 'suspended'
                                                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200'
                                                                    : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
                                                        }`}>
                                                            {license.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-800 dark:text-gray-200">
                                                        {license.expiresAt ? new Date(license.expiresAt).toLocaleDateString() : 'Never'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 dark:text-gray-200">
                                                        {license.activeServerCount || 0} / {license.serverLimit}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-xs space-x-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                        {license.status === 'active' ? (
                                                            <button
                                                                onClick={() => handleUpdateStatus(license.licenseKey, 'suspended')}
                                                                className="px-2.5 py-1.5 bg-yellow-500 text-white rounded hover:bg-yellow-600 font-medium cursor-pointer"
                                                            >
                                                                Suspend
                                                            </button>
                                                        ) : license.status === 'suspended' ? (
                                                            <button
                                                                onClick={() => handleUpdateStatus(license.licenseKey, 'active')}
                                                                className="px-2.5 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 font-medium cursor-pointer"
                                                            >
                                                                Reactivate
                                                            </button>
                                                        ) : null}

                                                        {license.status !== 'revoked' && (
                                                            <button
                                                                onClick={() => {
                                                                    setLicenseToRenew(license);
                                                                    setRenewDialogOpen(true);
                                                                }}
                                                                className="px-2.5 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium cursor-pointer"
                                                            >
                                                                Renew
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'machines' && (
                    <div className="space-y-4">
                        {/* Search and Filters */}
                        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-sm border border-gray-150/40 dark:border-gray-700/60 p-4">
                            <div className="max-w-md">
                                <input
                                    placeholder="Search machines by name, IP, fingerprint..."
                                    value={machinesSearchTerm}
                                    onChange={(e) => setMachinesSearchTerm(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                />
                            </div>
                        </div>

                        {serversLoading ? (
                            <div className="text-center py-8 text-gray-500">Loading machines...</div>
                        ) : filteredMachines.length === 0 ? (
                            <div className="text-center py-12 text-gray-500 bg-white dark:bg-gray-800 rounded-3xl border border-gray-150 dark:border-gray-700/60 font-medium">
                                {machinesSearchTerm ? 'No machines matched your search.' : 'No active servers registered for this customer.'}
                            </div>
                        ) : (
                            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-sm border border-gray-150 dark:border-gray-700/60 overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead className="bg-slate-50 dark:bg-slate-900/30">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Machine Name
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    License Key
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    IP Address
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Status
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Version
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Reactivations
                                                </th>
                                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                                            {filteredMachines.map((srv: any) => {
                                                const parentLicense = customerLicenses.find((l: any) => l.licenseKey === srv.licenseKey);
                                                return (
                                                    <tr
                                                        key={srv.serverId}
                                                        onClick={() => {
                                                            if (parentLicense) setSelectedLicense(parentLicense);
                                                        }}
                                                        className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/35 transition-colors"
                                                    >
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white">
                                                            {srv.serverName}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap font-mono text-xs text-gray-650 dark:text-gray-300">
                                                            {srv.licenseKey}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-855 dark:text-gray-200 font-semibold" onClick={(e) => e.stopPropagation()}>
                                                            {srv.ipAddress && srv.ipAddress !== 'unknown' ? (
                                                                <Tooltip title="View network details" arrow>
                                                                    <button
                                                                        onClick={() => setSelectedMachineForIpDetails(srv)}
                                                                        className="text-primary-650 dark:text-primary-400 hover:underline font-mono font-semibold cursor-pointer focus:outline-none"
                                                                    >
                                                                        {srv.ipAddress}
                                                                    </button>
                                                                </Tooltip>
                                                            ) : (
                                                                'N/A'
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <span className={`inline-block px-2.5 py-0.5 text-xs font-medium rounded-full ${
                                                                srv.status === 'active'
                                                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                                                                    : srv.status === 'suspicious'
                                                                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200'
                                                                        : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
                                                            }`}>
                                                                {srv.status}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-650 dark:text-gray-300">
                                                            {srv.version}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-800 dark:text-gray-200">
                                                            {srv.reactivationCount || 0}
                                                        </td>
                                                        <td className="px-6 py-4 text-right text-sm whitespace-nowrap no-row-click relative" onClick={(e) => e.stopPropagation()}>
                                                            {srv.status === 'migrated' ? (
                                                                <Tooltip title="Migrated machines cannot be modified" arrow>
                                                                    <span className="inline-block">
                                                                        <button
                                                                            disabled
                                                                            className="p-1 text-slate-300 dark:text-slate-600 cursor-not-allowed"
                                                                        >
                                                                            <MoreVertIcon className="!w-4 !h-4" />
                                                                        </button>
                                                                    </span>
                                                                </Tooltip>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setOpenMenuServerId(openMenuServerId === srv.serverId ? null : srv.serverId);
                                                                        }}
                                                                        className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-500 hover:text-slate-700 cursor-pointer"
                                                                    >
                                                                        <MoreVertIcon className="!w-4 !h-4" />
                                                                    </button>
                                                                    
                                                                    {openMenuServerId === srv.serverId && (
                                                                        <div className="absolute right-6 mt-1 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-150 dark:border-gray-700 z-50 py-1 text-left">
                                                                            {srv.status === 'deactivated' && (
                                                                                <>
                                                                                    <button
                                                                                        onClick={() => {
                                                                                            setActionServer(srv);
                                                                                            setActionType('reactivate');
                                                                                            setOpenMenuServerId(null);
                                                                                        }}
                                                                                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-green-600 dark:text-green-400 hover:bg-gray-100 dark:hover:bg-gray-700 font-semibold cursor-pointer"
                                                                                    >
                                                                                        <CheckIcon className="!w-4 !h-4 text-green-600 dark:text-green-400" />
                                                                                        Re-activate Machine
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={() => {
                                                                                            setActionServer(srv);
                                                                                            setActionType('replace');
                                                                                            setOpenMenuServerId(null);
                                                                                        }}
                                                                                        className="block w-full px-4 py-2 text-sm text-[#c87a53] hover:bg-gray-100 dark:hover:bg-gray-700 font-medium cursor-pointer"
                                                                                    >
                                                                                        Replace Machine
                                                                                    </button>
                                                                                </>
                                                                            )}

                                                                            {srv.status === 'blocked' && (
                                                                                <>
                                                                                    <button
                                                                                        onClick={() => {
                                                                                            setActionServer(srv);
                                                                                            setActionType('unblock');
                                                                                            setOpenMenuServerId(null);
                                                                                        }}
                                                                                        className="block w-full px-4 py-2 text-sm text-green-600 dark:text-green-400 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium cursor-pointer"
                                                                                    >
                                                                                        Unblock Machine
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={() => {
                                                                                            setActionServer(srv);
                                                                                            setActionType('deactivate');
                                                                                            setOpenMenuServerId(null);
                                                                                        }}
                                                                                        className="block w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                                                                                    >
                                                                                        Deactivate
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={() => {
                                                                                            setActionServer(srv);
                                                                                            setActionType('replace');
                                                                                            setOpenMenuServerId(null);
                                                                                        }}
                                                                                        className="block w-full px-4 py-2 text-sm text-[#c87a53] hover:bg-gray-100 dark:hover:bg-gray-700 font-medium cursor-pointer"
                                                                                    >
                                                                                        Replace Machine
                                                                                    </button>
                                                                                </>
                                                                            )}

                                                                            {(srv.status === 'active' || srv.status === 'suspicious') && (
                                                                                <>
                                                                                    <button
                                                                                        onClick={() => {
                                                                                            setActionServer(srv);
                                                                                            setActionType('block');
                                                                                            setOpenMenuServerId(null);
                                                                                        }}
                                                                                        className="block w-full px-4 py-2 text-sm text-red-655 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium cursor-pointer"
                                                                                    >
                                                                                        Block Machine
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={() => {
                                                                                            setActionServer(srv);
                                                                                            setActionType('deactivate');
                                                                                            setOpenMenuServerId(null);
                                                                                        }}
                                                                                        className="block w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                                                                                    >
                                                                                        Deactivate
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={() => {
                                                                                            setActionServer(srv);
                                                                                            setActionType('replace');
                                                                                            setOpenMenuServerId(null);
                                                                                        }}
                                                                                        className="block w-full px-4 py-2 text-sm text-[#c87a53] hover:bg-gray-100 dark:hover:bg-gray-700 font-medium cursor-pointer"
                                                                                    >
                                                                                        Replace Machine
                                                                                    </button>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'logs' && (
                    <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-sm border border-gray-150 dark:border-gray-700/60 p-6">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Activity Timeline Logs</h3>
                        {logsLoading ? (
                            <div className="text-center py-8 text-gray-500">Loading activity timeline...</div>
                        ) : logs.length === 0 ? (
                            <div className="text-center py-8 text-gray-400 font-medium">No activity found for this customer.</div>
                        ) : (
                            <>
                                <div className="space-y-6 relative border-l border-gray-250 dark:border-gray-700 pl-6 ml-3">
                                    {logs.map((log: any) => {
                                        let style = {
                                            bg: 'bg-gray-400',
                                            text: 'text-gray-700 dark:text-gray-305',
                                            border: 'border-gray-300 dark:border-gray-700',
                                            icon: '📝'
                                        };
                                        if (log.type === 'LICENSE_RENEWED') {
                                            style = {
                                                bg: 'bg-green-500',
                                                text: 'text-green-700 dark:text-green-300',
                                                border: 'border-green-355 dark:border-green-700',
                                                icon: '🔄'
                                            };
                                        } else if (log.type === 'LICENSE_CREATE') {
                                            style = {
                                                bg: 'bg-indigo-500',
                                                text: 'text-indigo-700 dark:text-indigo-300',
                                                border: 'border-indigo-300 dark:border-indigo-700',
                                                icon: '🆕'
                                            };
                                        } else if (log.type === 'LICENSE_UPDATE') {
                                            if (log.status === 'suspended') {
                                                style = {
                                                    bg: 'bg-orange-500',
                                                    text: 'text-orange-700 dark:text-orange-300',
                                                    border: 'border-orange-300 dark:border-orange-700',
                                                    icon: '⏸️'
                                                };
                                            } else {
                                                style = {
                                                    bg: 'bg-green-550',
                                                    text: 'text-green-700 dark:text-green-300',
                                                    border: 'border-green-300 dark:border-green-750',
                                                    icon: '▶️'
                                                };
                                            }
                                        } else if (log.type === 'SERVER_BLOCK' || log.type === 'SERVER_SUSPICIOUS') {
                                            style = {
                                                bg: 'bg-red-500',
                                                text: 'text-red-700 dark:text-red-300',
                                                border: 'border-red-300 dark:border-red-700',
                                                icon: '⚠️'
                                            };
                                        } else if (log.type === 'ACTIVATE') {
                                            style = {
                                                bg: 'bg-blue-500',
                                                text: 'text-blue-700 dark:text-blue-300',
                                                border: 'border-blue-300 dark:border-blue-755',
                                                icon: '⚙️'
                                            };
                                        }

                                        return (
                                            <div key={log.id} className="relative">
                                                <span className={`absolute -left-[35px] top-0.5 flex items-center justify-center w-6 h-6 rounded-full ${style.bg} border-2 ${style.border} text-xs shadow-sm`}>
                                                    {style.icon}
                                                </span>
                                                <div>
                                                    <div className="flex justify-between items-center">
                                                        <span className={`text-sm font-bold ${style.text}`}>{log.type}</span>
                                                        <span className="text-[10px] text-gray-400 font-mono">
                                                            {new Date(log.createdAt || log.timestamp).toLocaleString()}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-gray-500 dark:text-gray-455 mt-0.5">
                                                        {log.details || 'Action completed successfully.'}
                                                    </p>
                                                    {log.licenseKey && (
                                                        <p className="text-[10px] text-gray-400 mt-0.5 font-mono">
                                                            License: {log.licenseKey}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {logs.length > 0 && (
                                    <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                                        <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                                            Page <span className="font-semibold text-gray-800 dark:text-gray-200">{currentPageIndex + 1}</span>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => setCurrentPageIndex(Math.max(0, currentPageIndex - 1))}
                                                disabled={currentPageIndex === 0}
                                                className="px-3 py-1 bg-gray-150 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md disabled:opacity-50 text-sm font-medium hover:bg-gray-250 dark:hover:bg-gray-650 transition-colors cursor-pointer"
                                            >
                                                Prev
                                            </button>
                                            <button
                                                onClick={() => {
                                                    if (nextCursor) {
                                                        if (currentPageIndex + 1 >= cursorStack.length) {
                                                            setCursorStack([...cursorStack, nextCursor]);
                                                        }
                                                        setCurrentPageIndex(currentPageIndex + 1);
                                                    }
                                                }}
                                                disabled={!nextCursor}
                                                className="px-3 py-1 bg-gray-150 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md disabled:opacity-50 text-sm font-medium hover:bg-gray-250 dark:hover:bg-gray-650 transition-colors cursor-pointer"
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {activeTab === 'machine-network' && (
                    <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-sm border border-gray-150/40 dark:border-gray-700/60 p-6">
                        <div className="mb-4">
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white tracking-tight">
                                Machine Network & ISP Insights
                            </h2>
                            <p className="text-xs text-gray-400 mt-1">
                                ISP, ASN, and country location tracking for activated machines.
                            </p>
                        </div>

                        {/* Search and Filters Toolbar */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-150/40 dark:border-gray-700/60 p-4 mb-4 flex flex-wrap items-center gap-4">
                            {/* Search Bar */}
                            <div className="flex-1 min-w-[200px]">
                                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Search Insights</label>
                                <input
                                    type="text"
                                    placeholder="Search machine, IP, ISP, ASN..."
                                    value={networkSearchTerm}
                                    onChange={(e) => setNetworkSearchTerm(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-750 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                />
                            </div>

                            {/* License Filter */}
                            <div className="w-56">
                                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">License Filter</label>
                                <select
                                    value={networkLicenseFilter}
                                    onChange={(e) => setNetworkLicenseFilter(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-750 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                >
                                    <option value="">All Licenses</option>
                                    {customerLicenses.map((lic: any) => (
                                        <option key={lic.licenseKey} value={lic.licenseKey}>
                                            {lic.licenseKey.substring(0, 15)}... ({lic.productId})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Machine Filter (Searchable Autocomplete) */}
                            <div className="w-56 relative">
                                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Machine Filter</label>
                                <div
                                    onClick={() => setShowMachineDropdown(!showMachineDropdown)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-750 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus-within:ring-1 focus-within:ring-primary-500 cursor-pointer flex justify-between items-center"
                                >
                                    <span className="truncate">
                                        {networkMachineFilter
                                            ? (customerServers.find((s: any) => s.serverId === networkMachineFilter)?.serverName || networkMachineFilter)
                                            : 'All Machines'}
                                    </span>
                                    <span className="text-[10px] text-gray-400">▼</span>
                                </div>
                                {showMachineDropdown && (
                                    <>
                                        <div className="fixed inset-0 z-40" onClick={() => { setShowMachineDropdown(false); setMachineSearchQuery(''); }} />
                                        <div className="absolute right-0 mt-1 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-150 dark:border-gray-700 z-50 py-2 p-2 space-y-2">
                                            <input
                                                type="text"
                                                placeholder="Search machine..."
                                                value={machineSearchQuery}
                                                onChange={(e) => setMachineSearchQuery(e.target.value)}
                                                className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-xs focus:outline-none"
                                                onClick={(e) => e.stopPropagation()}
                                                autoFocus
                                            />
                                            <div className="max-h-48 overflow-y-auto space-y-0.5">
                                                <button
                                                    onClick={() => {
                                                        setNetworkMachineFilter('');
                                                        setShowMachineDropdown(false);
                                                        setMachineSearchQuery('');
                                                    }}
                                                    className="w-full text-left px-2 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md truncate font-medium"
                                                >
                                                    All Machines
                                                </button>
                                                {customerServers
                                                    .filter((s: any) => {
                                                        if (!machineSearchQuery) return true;
                                                        const query = machineSearchQuery.toLowerCase();
                                                        return (
                                                            (s.serverName || '').toLowerCase().includes(query) ||
                                                            (s.serverId || '').toLowerCase().includes(query)
                                                        );
                                                    })
                                                    .map((s: any) => (
                                                        <button
                                                            key={s.serverId}
                                                            onClick={() => {
                                                                setNetworkMachineFilter(s.serverId);
                                                                setShowMachineDropdown(false);
                                                                setMachineSearchQuery('');
                                                            }}
                                                            className={`w-full text-left px-2 py-1.5 text-xs rounded-md truncate ${
                                                                networkMachineFilter === s.serverId
                                                                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-semibold'
                                                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                                            }`}
                                                        >
                                                            {s.serverName || 'Unknown'} <span className="text-[10px] text-gray-400 font-mono">({s.serverId.substring(0, 8)})</span>
                                                        </button>
                                                    ))}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {insightsLoading || serversLoading ? (
                            <div className="text-center py-6 text-gray-500">Loading network insights...</div>
                        ) : !customerServers || customerServers.length === 0 ? (
                            <div className="text-center py-8 text-gray-400 font-medium bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-800/60 rounded-2xl">
                                No network data available
                            </div>
                        ) : filteredNetworkInsights.length === 0 ? (
                            <div className="text-center py-8 text-gray-400 font-medium bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-800/60 rounded-2xl">
                                No network insights matched your search / filters.
                            </div>
                        ) : (
                            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-150/40 dark:border-gray-700/60 overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead className="bg-slate-50 dark:bg-slate-900/30">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Machine
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Public IP
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    ISP
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    ASN
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Country
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Last Seen
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Last Updated
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                                            {paginatedNetworkInsights.map((item: any) => {
                                                return (
                                                    <tr key={item.machineId} className="hover:bg-slate-50 dark:hover:bg-slate-700/35 transition-colors">
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white">
                                                            {item.machineName}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center gap-2 font-mono text-sm text-slate-800 dark:text-slate-100 font-semibold">
                                                                {item.ipAddress && item.ipAddress !== 'unknown' ? (
                                                                    <Tooltip title="View network details" arrow>
                                                                        <button
                                                                            onClick={() => setSelectedMachineForIpDetails(item.rawMachine)}
                                                                            className="text-primary-650 dark:text-primary-400 hover:underline font-mono font-semibold cursor-pointer focus:outline-none"
                                                                        >
                                                                            {item.ipAddress}
                                                                        </button>
                                                                    </Tooltip>
                                                                ) : (
                                                                    'N/A'
                                                                )}
                                                                {item.ipAddress && item.ipAddress !== 'unknown' && (
                                                                    <Tooltip title="Copy IP" arrow>
                                                                        <button
                                                                            onClick={() => {
                                                                                navigator.clipboard.writeText(item.ipAddress);
                                                                                showMessage('IP address copied');
                                                                            }}
                                                                            className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded text-slate-500 cursor-pointer"
                                                                        >
                                                                            <ContentCopyIcon className="!w-3 !h-3" />
                                                                        </button>
                                                                    </Tooltip>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-650 dark:text-gray-300">
                                                            {item.isp || <span className="text-gray-400 italic">No network data available</span>}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-650 dark:text-gray-300">
                                                            {item.asn || '-'}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-650 dark:text-gray-300">
                                                            {item.country || '-'}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
                                                            {formatLastSeen(item.lastSeenAt)}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {item.lastCheckedAt ? timeAgo(item.lastCheckedAt) : '-'}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination Footer */}
                                {totalNetworkPages > 1 && (
                                    <div className="flex justify-between items-center px-6 py-4 bg-gray-50 dark:bg-gray-800/40 border-t border-gray-200 dark:border-gray-750">
                                        <div className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-4">
                                            <span>
                                                Showing <span className="font-semibold text-gray-800 dark:text-gray-250">{(networkCurrentPage - 1) * networkItemsPerPage + 1}–{Math.min(networkCurrentPage * networkItemsPerPage, totalNetworkItems)}</span> of{' '}
                                                <span className="font-semibold text-gray-800 dark:text-gray-250">{totalNetworkItems}</span>
                                            </span>
                                            <span className="text-xs text-gray-400 border-l border-gray-300 dark:border-gray-600 pl-4">
                                                Page {networkCurrentPage} of {totalNetworkPages}
                                            </span>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => setNetworkCurrentPage(Math.max(networkCurrentPage - 1, 1))}
                                                disabled={networkCurrentPage === 1}
                                                className="px-3 py-1 bg-gray-150 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md disabled:opacity-50 text-sm font-medium hover:bg-gray-250 dark:hover:bg-gray-650 transition-colors cursor-pointer"
                                            >
                                                Prev
                                            </button>
                                            <button
                                                onClick={() => setNetworkCurrentPage(Math.min(networkCurrentPage + 1, totalNetworkPages))}
                                                disabled={networkCurrentPage === totalNetworkPages}
                                                className="px-3 py-1 bg-gray-150 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md disabled:opacity-50 text-sm font-medium hover:bg-gray-250 dark:hover:bg-gray-650 transition-colors cursor-pointer"
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Reusable License Details Drawer */}
            <LicenseDetailsDrawer
                open={!!selectedLicense}
                onClose={() => setSelectedLicense(null)}
                license={selectedLicense}
                onLicenseUpdated={() => {
                    queryClient.invalidateQueries({ queryKey: ['licenses'] });
                }}
            />

            {/* Reusable Renew License Dialog */}
            <RenewLicenseDialog
                open={renewDialogOpen}
                onClose={() => setRenewDialogOpen(false)}
                license={licenseToRenew}
                onSuccess={() => {
                    queryClient.invalidateQueries({ queryKey: ['licenses'] });
                    queryClient.invalidateQueries({ queryKey: ['logs'] });
                    showMessage('License renewed successfully');
                    setRenewDialogOpen(false);
                    
                    // Refresh current active drawer if open
                    if (selectedLicense) {
                        const refreshed = licensesData?.licenses?.find((l: any) => l.licenseKey === selectedLicense.licenseKey);
                        if (refreshed) setSelectedLicense(refreshed);
                    }
                }}
            />

            {/* Reusable Machine Actions Manager */}
            <MachineActionsManager
                server={actionServer}
                action={actionType}
                onClose={() => {
                    setActionServer(null);
                    setActionType(null);
                }}
                onSuccess={(msg) => {
                    showMessage(msg);
                    // Reset logs pagination to first page on server actions to show the new log entry
                    setCursorStack([null]);
                    setCurrentPageIndex(0);
                }}
                onError={(err) => showMessage(err)}
            />

            {/* Machine IP Details Dialog */}
            <MachineIpDetailsDialog
                open={!!selectedMachineForIpDetails}
                onClose={() => setSelectedMachineForIpDetails(null)}
                machine={selectedMachineForIpDetails}
                onSuccess={() => {
                    queryClient.invalidateQueries({ queryKey: ['network-insights'] });
                }}
            />

            {/* Notification system */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
        </div>
    );
}

export default CustomerDetailPage;
